/**
 * HookPerformanceChart — compact right-rail widget.
 *
 * Tabbed by Hook | Tone | Visual, shows ranked bars of LTV value per dimension
 * value with secondary CTR and LTV-ROAS badges. Designed to fill the aside
 * column below the PersonaLanesRail.
 */
import { useEffect, useState } from 'react';
import { Sparkles, Trophy } from 'lucide-react';
import { clsx } from 'clsx';
import {
  creativeWorkbenchApi,
  type WorkbenchFormulaBreakdownResponse,
  type WorkbenchFormulaBar,
  type WorkbenchFormulaDimension,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';

type Tab = { key: WorkbenchFormulaDimension; label: string };
const TABS: Tab[] = [
  { key: 'hook_type', label: 'Hook' },
  { key: 'tone', label: 'Tone' },
  { key: 'visual_style', label: 'Visual' },
];

const STRENGTH_BAR: Record<string, string> = {
  strong: 'bg-teal-500',
  promising: 'bg-emerald-400',
  weak: 'bg-slate-300',
  unknown: 'bg-slate-200',
};

const STRENGTH_BADGE: Record<string, string> = {
  strong: 'bg-teal-50 text-teal-700 ring-teal-200',
  promising: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  weak: 'bg-slate-50 text-slate-500 ring-slate-200',
  unknown: 'bg-slate-50 text-slate-400 ring-slate-100',
};

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

function CompactBar({ bar, isTop }: { bar: WorkbenchFormulaBar; isTop: boolean }) {
  return (
    <div className="group space-y-0.5">
      <div className="flex items-center gap-2">
        {/* Label */}
        <div className="flex items-center gap-1 w-28 shrink-0 min-w-0">
          {isTop && <Trophy size={9} className="text-teal-500 shrink-0" />}
          <span
            className="text-[11px] font-semibold text-slate-700 capitalize truncate"
            title={bar.label}
          >
            {bar.label}
          </span>
        </div>

        {/* Bar */}
        <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={clsx(
              'h-full rounded-full transition-all duration-500',
              STRENGTH_BAR[bar.strength] ?? 'bg-slate-300',
            )}
            style={{ width: `${Math.max(bar.bar_width_pct, 3)}%` }}
          />
        </div>

        {/* Value */}
        <span className="text-[11px] font-bold text-slate-700 w-10 text-right shrink-0">
          {bar.estimated_value > 0 ? CURRENCY.format(bar.estimated_value) : `$${(bar.spend / 1000).toFixed(1)}k`}
        </span>
      </div>

      {/* Hover detail row */}
      <div className="hidden group-hover:flex items-center gap-2 pl-[7.5rem] text-[9px] font-mono text-slate-400">
        <span>{bar.ctr.toFixed(1)}% CTR</span>
        <span className="text-slate-300">·</span>
        <span>{bar.ltv_roas.toFixed(2)}x ROAS</span>
        <span className="text-slate-300">·</span>
        <span>{bar.ad_count} ads</span>
      </div>
    </div>
  );
}

function BreakdownPane({ data }: { data: WorkbenchFormulaBreakdownResponse }) {
  const { bars, winner } = data;
  const topBars = bars.slice(0, 7);

  // Summary metrics
  const totalVal = bars.reduce((s, b) => s + b.estimated_value, 0);
  const topBar = bars.find((b) => b.label === winner);

  return (
    <div className="space-y-3">
      {/* Insight pill */}
      {topBar && (
        <div className="flex items-start gap-1.5 text-[10px] text-slate-600 bg-teal-50 border border-teal-100 rounded-lg px-2.5 py-2">
          <Sparkles size={10} className="text-teal-500 mt-0.5 shrink-0" />
          <span>
            <strong className="capitalize">{topBar.label}</strong> generates{' '}
            {totalVal > 0
              ? `${Math.round((topBar.estimated_value / totalVal) * 100)}% of LTV value`
              : 'the most spend'}{' '}
            at <strong>{topBar.ltv_roas.toFixed(2)}x</strong> LTV-ROAS.
          </span>
        </div>
      )}

      {/* Bars */}
      <div className="space-y-2">
        {topBars.map((bar, idx) => (
          <CompactBar key={bar.label} bar={bar} isTop={idx === 0} />
        ))}
      </div>

      {/* Strength legend */}
      <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-slate-100">
        {(['strong', 'promising', 'weak'] as const).map((s) => (
          <span
            key={s}
            className={clsx(
              'text-[9px] font-semibold px-1.5 py-0.5 rounded-full ring-1 capitalize',
              STRENGTH_BADGE[s],
            )}
          >
            {s}
          </span>
        ))}
        <span className="text-[9px] text-slate-400 ml-auto">
          LTV-ROAS: ≥2x strong, ≥0.5x promising
        </span>
      </div>
    </div>
  );
}

export function HookPerformanceChart() {
  const { clientUid, personaId } = useCreativeWorkbench();
  const [activeDim, setActiveDim] = useState<WorkbenchFormulaDimension>('hook_type');
  const [data, setData] = useState<WorkbenchFormulaBreakdownResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getFormulaBreakdown({ clientUid, dimension: activeDim, personaId })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientUid, personaId, activeDim]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-[12px] font-bold text-navy-900 flex items-center gap-1.5">
          <Sparkles size={12} className="text-teal-500" />
          Hook Performance
        </h3>
      </div>

      {/* Dimension tabs */}
      <div className="flex items-center gap-1">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveDim(tab.key)}
            className={clsx(
              'flex-1 py-1 text-[10px] font-semibold rounded-md transition-colors',
              activeDim === tab.key
                ? 'bg-teal-50 text-teal-700 ring-1 ring-teal-200'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading && !data && (
        <div className="text-xs text-slate-400 text-center py-6">Loading...</div>
      )}

      {!loading && !data && (
        <div className="text-xs text-slate-400 text-center py-6">No data.</div>
      )}

      {data && <BreakdownPane data={data} />}
    </div>
  );
}
